import { isLoggedIn } from 'src/services/AuthenticationService';
import { logger } from 'src/services/Logger';
import { buildObservabilityUserTenantContext } from 'src/services/ObservabilityContextHelper';
import { redactContext } from 'src/services/ObservabilityRedactionHelper';
import { getUserTenant } from 'src/services/TenantsService';
import { getUserMetadata } from 'src/services/UserService';

type TCoralogixRum = (typeof import('@coralogix/browser'))['CoralogixRum'];

type TCoralogixRumServiceConfig = {
	application: string;
	environment: string;
	publicKey: string | undefined;
	version: string;
};

let coralogixRumPromise: Promise<TCoralogixRum> | undefined;

const getCoralogixRum = async (): Promise<TCoralogixRum> => {
	if (!coralogixRumPromise) {
		coralogixRumPromise = import('@coralogix/browser').then(({ CoralogixRum }) => CoralogixRum);
	}

	return coralogixRumPromise;
};

class CoralogixRumService {
	private initialized = false;

	private readonly config: TCoralogixRumServiceConfig;

	constructor(config: TCoralogixRumServiceConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		try {
			if (this.initialized) {
				return;
			}

			if (!this.config.publicKey) {
				logger.warn('Coralogix RUM: Public key not configured, skipping initialization');
				return;
			}

			if (!isLoggedIn()) {
				return;
			}

			const userInfo = await getUserMetadata();
			const tenantInfo = await getUserTenant();
			const userContext = buildObservabilityUserTenantContext(userInfo, tenantInfo);
			const CoralogixRum = await getCoralogixRum();

			CoralogixRum.init({
				public_key: this.config.publicKey,
				application: this.config.application,
				version: this.config.version,
				environment: this.config.environment,
				coralogixDomain: 'US2',
				user_context: {
					user_id: userContext.userId,
					user_name: userContext.userName,
					user_email: userContext.userEmail,
					user_metadata: userContext.userMetadata
				},
				sessionSampleRate: this.config.environment === 'production' ? 100 : 0,
				instrumentations: {
					errors: true,
					interactions: true,
					long_tasks: true,
					fetch: true,
					xhr: true,
					resources: true,
					web_vitals: true
				},
				sessionRecordingConfig: {
					enable: this.config.environment === 'production',
					autoStartSessionRecording: true,
					recordConsoleEvents: true,
					sessionRecordingSampleRate: 100
				}
			});

			this.initialized = true;

			logger.info('Coralogix RUM initialized successfully', {
				application: this.config.application,
				version: this.config.version,
				environment: this.config.environment
			});
		} catch (err) {
			logger.error('Error initializing Coralogix RUM', err);
		}
	}

	captureError(error: Error, context?: Record<string, unknown>): void {
		getCoralogixRum()
			.then((CoralogixRum) => {
				CoralogixRum.captureError(error, redactContext(context));
			})
			.catch((err) => {
				logger.error('Error logging to Coralogix RUM', err);
			});
	}

	addTiming(name: string, duration?: number): void {
		getCoralogixRum()
			.then((CoralogixRum) => {
				CoralogixRum.addTiming(name, duration);
			})
			.catch((err) => {
				logger.error('Error adding custom timing to Coralogix RUM', err);
			});
	}

	sendCustomMeasurement(name: string, value: number): void {
		getCoralogixRum()
			.then((CoralogixRum) => {
				CoralogixRum.sendCustomMeasurement(name, value);
			})
			.catch((err) => {
				logger.error('Error sending custom measurement to Coralogix RUM', err);
			});
	}
}

const coralogixRumService = new CoralogixRumService({
	application: 'site-scanner',
	environment: import.meta.env.VITE_RUN_ENV || 'development',
	publicKey: import.meta.env.VITE_CORALOGIX_RUM_PUBLIC_KEY,
	version: import.meta.env.VITE_UI_VERSION || 'unknown'
});

/**
 * Initializing Coralogix RUM service for error tracking, performance monitoring, and bug detection
 */
export const initCoralogixRum = async (): Promise<void> => {
	await coralogixRumService.init();
};
